import { spacing, tabletQuery } from '@globals/themes/themeConstants'
import { TenantMember, getTenantMember } from '@kudos/http-client'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import UserAvatar from '@shared/avatar/UserAvatar'
import { getUserDisplayName } from '@shared/userDisplay/UserNameDisplay'
import { useQuery } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import React, { useEffect } from 'react'

const popperStyle: SxProps = { zIndex: 3, position: 'absolute', borderRadius: spacing(2) }

export const getMentionPreviewRegionId = (tenantMemberId: string) =>
  `mention-preview-${tenantMemberId}`

type MentionsPopperProps = {
  tenantMemberId: string
  anchorEl: HTMLElement | null
  open: boolean
  onPreviewLoadingChange?: (loading: boolean) => void
}

const MentionsPopper: React.FC<MentionsPopperProps> = ({
  tenantMemberId,
  anchorEl,
  open,
  onPreviewLoadingChange,
}) => {
  const t = useT()
  const {
    data: tenantMember,
    isLoading,
    isFetching,
  } = useQuery<TenantMember>({
    queryKey: ['getMentionedTenantMember', tenantMemberId],
    queryFn: () => getTenantMember(tenantMemberId, { embeds: ['avatar', 'publicStaffProfile'] }),
    staleTime: 60 * 60 * 1000, // Prevent frequent api calls
  })

  const isTablet = useMediaQuery(tabletQuery)

  const { avatarUrls, firstName, lastName, publicStaffProfile } = tenantMember ?? {}
  const fullName = getUserDisplayName(tenantMember, t)

  useEffect(() => {
    onPreviewLoadingChange?.(isLoading || isFetching)
    return () => onPreviewLoadingChange?.(false)
  }, [isLoading, isFetching, onPreviewLoadingChange])

  if (!open) {
    return
  }

  const previewRegionProps = {
    id: getMentionPreviewRegionId(tenantMemberId),
    role: 'region' as const,
    'aria-label': t('Profile preview'),
  }

  if (isLoading || isFetching) {
    return (
      <Popper open={open} anchorEl={anchorEl} placement="top" sx={popperStyle}>
        <Box
          component={Paper}
          {...previewRegionProps}
          aria-busy
          sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={24} aria-hidden />
        </Box>
      </Popper>
    )
  }

  return (
    <Popper open={open} anchorEl={anchorEl} placement="top" sx={popperStyle}>
      <Box component={Paper} {...previewRegionProps} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack useFlexGap spacing={2} direction="row" alignItems="center">
            <UserAvatar
              tenantMemberId={tenantMemberId}
              media={avatarUrls}
              firstName={firstName}
              lastName={lastName}
              size={36}
            />
            <Typography variant="body2">{fullName}</Typography>
          </Stack>
          <Stack direction={isTablet ? 'column' : 'row'} useFlexGap spacing={1}>
            <Stack useFlexGap direction="row" spacing={1}>
              <WorkOutlineOutlinedIcon aria-hidden />
              <Typography>{publicStaffProfile?.jobTitle ?? '-'}</Typography>
            </Stack>
            <Stack useFlexGap direction="row" spacing={1}>
              <LocationOnOutlinedIcon aria-hidden />
              <Typography>{publicStaffProfile?.locations?.[0]?.name ?? '-'}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Popper>
  )
}

export default MentionsPopper
